import { z } from "zod";
import { LatLon, NumParam, Slug } from "./common.ts";

export const TERRAIN_RADIUS_MIN = 20;
export const TERRAIN_RADIUS_MAX = 300;
export const TERRAIN_RADIUS_DEFAULT = 150;
/** Server must downsample (raise cellSizeM) so neither side exceeds this. */
export const TERRAIN_MAX_SIDE = 512;

/**
 * GET /api/terrain?lat=&lon=&radius= → application/vnd.divespots.terrain (see codec).
 * - Point in no known lake → 200 land-only tile with lakeSlug/waterLevelM null.
 * - Tile with ZERO data cells (no bathy and no land coverage) → 404 not_found.
 *   So a served tile always has min/max over ≥ 1 cell.
 */
export const TerrainQuery = z
  .object({
    lat: NumParam.pipe(z.number().min(-90).max(90)),
    lon: NumParam.pipe(z.number().min(-180).max(180)),
    radius: NumParam.pipe(z.number().int().min(TERRAIN_RADIUS_MIN).max(TERRAIN_RADIUS_MAX)).default(
      TERRAIN_RADIUS_DEFAULT,
    ),
  })
  .strict();
export type TerrainQuery = z.infer<typeof TerrainQuery>;

export const TERRAIN_SOURCE = { nodata: 0, bathy: 1, land: 2 } as const;

export const TerrainMeta = z
  .object({
    width: z.number().int().min(2).max(TERRAIN_MAX_SIDE),
    height: z.number().int().min(2).max(TERRAIN_MAX_SIDE),
    cellSizeM: z.number().positive(),
    /** LV95 of the CENTRE of cell (row 0, col 0) = north-west corner. Rows go south, cols go east. */
    originLv95: z.object({ e: z.number(), n: z.number() }).strict(),
    /** Requested centre, echoed back. */
    center: LatLon,
    radiusM: z.number().int(),
    lakeSlug: Slug.nullable(),
    /** LN02 metres; null when the area is not in a known lake, and always null in dive tiles. */
    waterLevelM: z.number().nullable(),
    /** Over non-nodata cells. */
    minElevM: z.number(),
    maxElevM: z.number(),
    attribution: z.string(),
  })
  .strict()
  .refine((m) => m.minElevM <= m.maxElevM, { message: "minElevM > maxElevM", path: ["minElevM"] });
export type TerrainMeta = z.infer<typeof TerrainMeta>;

export const TERRAIN_CONTENT_TYPE = "application/vnd.divespots.terrain";
